import { supportedLanguages, supportedLanguageOptions, mapSystemLanguageToSupported, calculateEffectiveLanguage } from '../lib/i18n';

// Re-export the function for other modules
export { mapSystemLanguageToSupported } from '../lib/i18n';

/**
 * 检查是否为首次用户（没有语言偏好设置）
 */
export const isFirstTimeUser = (): boolean => {
  const hasLocalStorage = localStorage.getItem('i18nextLng');
  const hasUserPreference = localStorage.getItem('userLanguagePreference');
  return !hasLocalStorage && !hasUserPreference;
};

/**
 * 获取系统语言
 */
export const getSystemLanguage = (): string => {
  return navigator.language || 'zh';
};

/**
 * 获取当前有效语言（考虑用户偏好和系统语言）
 */
export const getCurrentEffectiveLanguage = (userPreference?: string): keyof typeof supportedLanguages => {
  if (userPreference) {
    return calculateEffectiveLanguage(userPreference);
  }
  
  // 如果没有用户偏好，检查 localStorage
  const storedLanguage = localStorage.getItem('i18nextLng');
  if (storedLanguage) {
    return calculateEffectiveLanguage(storedLanguage);
  }
  
  // 最后回退到系统语言
  return mapSystemLanguageToSupported(getSystemLanguage());
};

/**
 * 设置语言偏好到 localStorage
 */
export const setLanguagePreference = (language: string): void => {
  localStorage.setItem('userLanguagePreference', language);
  
  // 如果是 system，存储实际的系统语言到 i18nextLng
  if (language === 'system') {
    const effectiveLanguage = mapSystemLanguageToSupported(getSystemLanguage());
    localStorage.setItem('i18nextLng', effectiveLanguage);
  } else {
    localStorage.setItem('i18nextLng', language);
  }
};

/**
 * 获取用户语言偏好（区分用户选择和自动检测）
 */
export const getUserLanguagePreference = (): string | null => {
  return localStorage.getItem('userLanguagePreference');
};

/**
 * 获取语言显示名称（包含系统语言信息）
 * 注意：这个函数需要在组件内部使用，因为它需要访问 i18n 实例
 */
export const getLanguageDisplayName = (language: string, t?: (key: string, options?: any) => string): string => {
  if (language === 'system') {
    const systemLang = getSystemLanguage();
    const effectiveLang = mapSystemLanguageToSupported(systemLang);
    const systemLangName = supportedLanguages[effectiveLang];

    // 如果提供了翻译函数，使用翻译
    if (t) {
      const followSystemText = t('v3/me:appearance.followSystem', { defaultValue: '跟随系统' });
      return `${followSystemText} (${systemLangName})`;
    }

    // 回退到硬编码（用于非组件环境）
    return `跟随系统 (${systemLangName})`;
  }

  return supportedLanguageOptions[language as keyof typeof supportedLanguageOptions] || language;
};

/**
 * 检查是否有用户语言偏好设置
 */
export const hasUserLanguagePreference = (): boolean => {
  return !!localStorage.getItem('userLanguagePreference');
};

/**
 * 清除语言偏好设置（用于重置）
 */
export const clearLanguagePreference = (): void => {
  localStorage.removeItem('userLanguagePreference');
  localStorage.removeItem('i18nextLng');
};

/**
 * 初始化语言设置（仅在首次访问时）
 */
export const initializeLanguageIfNeeded = (detectedLanguage?: string): void => {
  if (!hasUserLanguagePreference() && detectedLanguage) {
    setLanguagePreference(detectedLanguage);
  }
};
